// ============================================================================
// DELIVERY MILESTONES API CLIENT
// ============================================================================

import { api } from './api';
import { Milestone, MilestoneStatus } from '../types/raid';

export interface MilestoneInput {
  name: string;
  sequence: number;
  description?: string;
  targetDate?: string;
  status?: MilestoneStatus;
}

/** Thrown when a delete is refused because items are still tagged. */
export class MilestoneInUseError extends Error {
  constructor(message: string, public readonly assigned: { stories: number; features: number }) {
    super(message);
    this.name = 'MilestoneInUseError';
  }
}

export async function listMilestones(projectId: string): Promise<Milestone[]> {
  const res = await api.get(`/api/projects/${projectId}/milestones`);
  return res.data.milestones ?? [];
}

export async function createMilestone(projectId: string, input: MilestoneInput): Promise<Milestone> {
  const res = await api.post(`/api/projects/${projectId}/milestones`, input);
  return res.data.milestone;
}

export async function updateMilestone(
  projectId: string, milestoneId: string, patch: Partial<MilestoneInput>
): Promise<Milestone> {
  const res = await api.patch(`/api/projects/${projectId}/milestones/${milestoneId}`, patch);
  return res.data.milestone;
}

/**
 * Deletes a milestone. Without `force` the server refuses when items are still
 * tagged and reports how many, so the UI can warn before untagging work spread
 * across sessions the user is not looking at.
 */
export async function deleteMilestone(
  projectId: string, milestoneId: string, force = false
): Promise<{ untagged: number }> {
  try {
    const res = await api.delete(
      `/api/projects/${projectId}/milestones/${milestoneId}`,
      { params: force ? { force: 'true' } : undefined }
    );
    return { untagged: res.data.untagged ?? 0 };
  } catch (error: any) {
    if (error?.response?.status === 409) {
      const { error: message, assigned } = error.response.data;
      throw new MilestoneInUseError(message, assigned ?? { stories: 0, features: 0 });
    }
    throw error;
  }
}

export interface MilestoneContents {
  milestone: Milestone;
  storyCount: number;
  featureCount: number;
  stories: any[];
  features: any[];
}

export async function getMilestoneContents(projectId: string, milestoneId: string): Promise<MilestoneContents> {
  const res = await api.get(`/api/projects/${projectId}/milestones/${milestoneId}/contents`);
  return res.data;
}

export async function assignToMilestone(
  projectId: string,
  milestoneId: string,
  body: { storyIds?: string[]; featureIds?: string[]; sessionId?: string }
): Promise<{ assigned: number; failures: { id: string; error: string }[] }> {
  const res = await api.post(`/api/projects/${projectId}/milestones/${milestoneId}/assign`, body);
  return { assigned: res.data.assigned ?? 0, failures: res.data.failures ?? [] };
}
